
import React, { useRef, useEffect } from 'react'
import CameraControls from 'camera-controls'
import { extend, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useScene } from '../../hooks/useWebCrafter.jsx'

// Install CameraControls with THREE.js
CameraControls.install({ THREE })

// Extend R3F with CameraControls
extend({ CameraControls })

/**
 * Default camera control settings
 */
const CONTROL_SETTINGS = {
  azimuthRotateSpeed: 0.25,
  polarRotateSpeed: 0.25,
  truckSpeed: 1,
  dollySpeed: 1,
}

/**
 * Camera controls component using camera-controls library.
 * Provides orbit controls with smooth damping.
 * 
 * @param {Object} props
 * @param {React.RefObject} props.innerRef - Ref to expose the controls instance
 * @param {Object} [props.rest] - Additional props passed to cameraControls
 */
export function Controls({ innerRef, ...rest })
{
  const camera = useThree(state => state.camera)
  const gl = useThree(state => state.gl)
  const { size } = useThree()

  const { cameraManager } = useScene()

  // Update controls each frame
  useFrame((state, delta) =>
  {
    cameraManager?.update()
    innerRef.current?.update(delta)
  })

  // Apply control settings on mount
  useEffect(() =>
  {
    const controls = innerRef.current
    if (!controls) return

    controls.azimuthRotateSpeed = CONTROL_SETTINGS.azimuthRotateSpeed
    controls.polarRotateSpeed = CONTROL_SETTINGS.polarRotateSpeed
    controls.truckSpeed = CONTROL_SETTINGS.truckSpeed
    controls.dollySpeed = CONTROL_SETTINGS.dollySpeed
  }, [ innerRef ])

  // Handle double-click to move camera target
  useEffect(() =>
  {
    const handleDoubleClick = (event) =>
    {
      if (!cameraManager || !innerRef.current) return

      // Convert mouse position to normalized device coordinates (-1 to 1)
      const mouse = new THREE.Vector2()
      mouse.x = (event.clientX / size.width) * 2 - 1
      mouse.y = -(event.clientY / size.height) * 2 + 1

      cameraManager.handleDoubleClick(mouse)
    }

    const canvas = gl.domElement
    canvas.addEventListener('dblclick', handleDoubleClick)

    return () =>
    {
      canvas.removeEventListener('dblclick', handleDoubleClick)
    }
  }, [ cameraManager, innerRef, gl.domElement, size ])

  return (
    <cameraControls
      ref={innerRef}
      args={[ camera, gl.domElement ]}
      {...rest}
    />
  )
}

export default Controls
